import {
  Box,
  CardMedia,
  Chip,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { ClockIcon } from 'src/v3/core/icons'
import { SessionCardProps } from '../types'

export let SessionCard = ({
  session,
  isLive,
  isUpcoming,
  timeSlot,
}: SessionCardProps) => {
  let navigate = useNavigate()
  let [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  // Convert UTC time to user's local time for display
  let displayTime = new Date(`${session.sessionTime}Z`).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  })

  let handleClick = () => {
    navigate(`/events/${session.eventID}`)
  }

  let handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      navigate(`/events/${session.eventID}`)
    }
  }

  let handleMenuClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget as HTMLElement)
  }

  let handleMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation()
      event.preventDefault()
      setMenuAnchor(event.currentTarget as HTMLElement)
    }
  }

  let handleMenuClose = () => {
    setMenuAnchor(null)
  }

  let handleGoToEvent = () => {
    navigate(`/events/${session.eventID}`)
    handleMenuClose()
  }

  let formatInstructors = (instructorNames: string[]) => {
    if (instructorNames.length === 0) return 'TBA'
    if (instructorNames.length === 1) return instructorNames[0]
    if (instructorNames.length === 2) return instructorNames.join(' & ')
    return `${instructorNames[0]} & ${instructorNames.length - 1} others`
  }

  // Use actual thumbnail from API or fallback to placeholder
  let thumbnailUrl = session.thumbnail
    ? `${import.meta.env.VITE_CLOUDFRONT_URL}/images/${session.thumbnail.replace('originals', 'resized')}`
    : `${import.meta.env.VITE_CLOUDFRONT_URL}/images/placeholder-class.jpg`

  let statusText = isLive ? 'Live now' : isUpcoming ? 'Starting soon' : ''
  let ariaLabel = `${session.name}, ${timeSlot} session at ${displayTime}, ${session.category}, ${session.hours || 0} CE hours, with ${formatInstructors(session.instructorNames)}${statusText ? `, ${statusText}` : ''}`

  return (
    <Box
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={ariaLabel}
      sx={{
        width: '100%',
        cursor: 'pointer',
        transition: 'transform 0.3s ease',
        '&:hover': {
          transform: 'scale(1.05)',
        },
        '&:focus': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
        },
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16/9',
          overflow: 'hidden',
          borderRadius: 2,
          bgcolor: '#000000',
        }}
      >
        <CardMedia
          component="img"
          image={thumbnailUrl}
          alt=""
          role="presentation"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />

        {/* Top Left - Session Time */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
          }}
          aria-hidden="true"
        >
          <Chip
            icon={<ClockIcon />}
            label={displayTime}
            size="small"
            sx={{
              bgcolor: 'rgba(0,0,0,0.7)',
              color: 'white',
              fontSize: 12,
              '& .MuiChip-icon': {
                color: 'white',
              },
            }}
          />
        </Box>

        {/* Top Right - Ellipsis Menu */}
        <IconButton
          size="small"
          onClick={handleMenuClick}
          onKeyDown={handleMenuKeyDown}
          aria-label={`More options for ${session.name}`}
          aria-haspopup="true"
          aria-expanded={Boolean(menuAnchor)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'rgba(255,255,255,0.9)',
            bgcolor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            '&:hover': {
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.8)',
            },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>

        {/* Bottom Right - Live Status */}
        {(isLive || isUpcoming) && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
            }}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            {isLive && (
              <Chip
                label="LIVE NOW"
                size="small"
                color="error"
                sx={{
                  fontWeight: 600,
                  fontSize: 12,
                }}
              />
            )}
            {isUpcoming && !isLive && (
              <Chip
                label="Starting Soon"
                size="small"
                color="warning"
                sx={{
                  fontWeight: 600,
                  fontSize: 12,
                }}
              />
            )}
          </Box>
        )}
      </Box>
      {/* Menu Popover */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'rgba(0,0,0,0.9)',
              color: 'white',
              minWidth: 220,
            },
          },
          list: {
            'aria-label': `Options for ${session.name}`,
          },
        }}
      >
        <MenuItem disabled sx={{ opacity: 0.7 }} aria-hidden="true">
          <Typography variant="body2">{session.name}</Typography>
        </MenuItem>
        <MenuItem disabled sx={{ opacity: 0.7 }} aria-hidden="true">
          <Typography variant="body2">{session.category}</Typography>
        </MenuItem>
        {session.hours && (
          <MenuItem disabled sx={{ opacity: 0.7 }} aria-hidden="true">
            <Typography variant="body2">
              {session.hours} hour{session.hours === 1 ? '' : 's'}
            </Typography>
          </MenuItem>
        )}
        <MenuItem disabled sx={{ opacity: 0.7 }} aria-hidden="true">
          <Typography variant="body2">
            By {formatInstructors(session.instructorNames)}
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={handleGoToEvent}
          aria-label={`View details for ${session.name}`}
        >
          <ListItemIcon>
            <PlayArrowIcon sx={{ color: 'white' }} aria-hidden="true" />
          </ListItemIcon>
          <ListItemText primary="Go to event" />
        </MenuItem>
      </Menu>
    </Box>
  )
}
